import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from '../../i18n';
import fr from '../../locales/fr.json';
import en from '../../locales/en.json';
import ar from '../../locales/ar.json';

type Language = 'fr' | 'en' | 'ar';

interface I18nContextType {
  language: Language;
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  t: (path: string, params?: Record<string, any>) => any;
  isRTL: boolean;
  i18n: any;
}

const locales: Record<Language, any> = { fr, en, ar };

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('app-language');
    return (saved as Language) || 'fr';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
    // Sync with i18next
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  };

  const isRTL = language === 'ar';

  useEffect(() => {
    // Listen to i18next changes to sync this context
    const handleLanguageChange = (lng: string) => {
      const baseLng = lng.split('-')[0] as Language;
      if (['en', 'fr', 'ar'].includes(baseLng)) {
        setLanguageState(baseLng);
      } else {
        setLanguageState(lng as Language);
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const t = (path: string, params?: Record<string, any>): any => {
    const keys = path.split('.');
    const baseLanguage = language.split('-')[0] as Language;
    let value = locales[baseLanguage] || locales[language] || locales['fr'];

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        console.warn(`[I18nContext] Translation key not found: "${path}" for language: "${language}"`);
        return path;
      }
    }

    if (typeof value === 'string' && params) {
      return Object.entries(params).reduce((acc, [key, val]) => {
        return acc.split(`{${key}}`).join(String(val));
      }, value);
    }

    return value ?? path;
  };

  return (
    <I18nContext.Provider value={{ language, currentLanguage: language, setLanguage, t, isRTL, i18n }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};
